/* eslint-disable no-unused-vars */
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Card, CardBody, TabContent, TabPane } from 'reactstrap';
import Avatar from 'react-avatar';
import { getAuthUser } from '../../../utils/auth.util';
import {
  fetchEmployeeDetails,
  fetchEmployeeProfilePicture,
} from '../../../store/apis/employeeService';
import { iconsProfileEmail, iconsProfilePhone } from '../../../assets';
import impersonateUserIcon from '../../../assets/icons/impersonate-user.svg';
import './ProfileWidget.scss';
import ProfileDemoTab from './ProfileDemoTab';
import WorkReportTab from './WorkReportTab';
import ChangeEmployeeModal from './ChangeEmployeeModal/ChangeEmployeeModal';
import { getCompanyCode } from '../../../config/axios.config';

function ProfileWidget() {
  const [activeTab, setActiveTab] = useState('1');
  const { id, employeeId } = getAuthUser();
  const companyCode = getCompanyCode();
  const [changeEmpModal, setChangeEmpModal] = useState(false);
  const { data: employeeDetails } = useQuery({
    queryKey: ['employeeDetails'],
    queryFn: () =>
      fetchEmployeeDetails({
        type: 'PROFILE',
      }),
    refetchOnWindowFocus: false,
    staleTime: 0, // Data is always stale, so refetches on mount
    cacheTime: 0,
  });

  const { data: employeeContact } = useQuery({
    queryKey: ['employeeContact'],
    queryFn: () =>
      fetchEmployeeDetails({
        type: 'LOCAL_ADDRESS',
      }),
    refetchOnWindowFocus: false,
  });

  const { data: employeeDocumentation } = useQuery({
    queryKey: ['employeeDocumentation'],
    queryFn: () =>
      fetchEmployeeDetails({
        type: 'DOCUMENTS',
      }),
    refetchOnWindowFocus: false,
  });

  const { data: profilePicture } = useQuery({
    queryKey: ['profilePicture'],
    queryFn: () =>
      fetchEmployeeProfilePicture({
        userId: id,
        employeeId,
        companyCode,
      }),
  });

  const toggle = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const visaDoc = employeeDocumentation?.find(
    doc => doc.DocumentType === 'Visa',
  );
  const passportDoc = employeeDocumentation?.find(
    doc => doc.DocumentType === 'Passport',
  );

  return (
    <Card className="profile-widget">
      <CardBody
        className="profile-widget-body p-0"
        style={{ overflow: 'hidden' }}
      >
        {/* Header */}
        <div className="profile-widget-header">
          <div className="d-flex flex-row align-items-center gap-2">
            <div>
              <Avatar
                round
                name={employeeDetails?.[0]?.EmployeeName?.trim() || 'Unknown'}
                size="2.85rem"
                color="#CFCBDC"
                fgColor="#475467"
                maxInitials={2}
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                }}
                src={profilePicture}
              />
            </div>
            <div className="d-flex flex-column align-items-start">
              <div className="d-flex flex-row align-items-center gap-2">
                <div
                  className="profile-widget-name fs-5"
                  title={employeeDetails?.[0]?.EmployeeName?.trim()}
                >
                  {employeeDetails?.[0]?.EmployeeName?.trim()}
                </div>
                <div className="d-flex flex-row align-items-center gap-2">
                  <div
                    className={`employee-badge ${getAuthUser()?.canViewOtherEmp ? 'cursor-pointer text-decoration-underline' : ''}`}
                    onClick={() => {
                      if (getAuthUser()?.canViewOtherEmp) {
                        setChangeEmpModal(true);
                      }
                    }}
                  >
                    {employeeDetails?.[0]?.EmployeeNo?.trim()}
                  </div>
                </div>
                {getAuthUser()?.userName?.trim()?.toLowerCase() !==
                  getAuthUser()?.employeeId?.trim()?.toLowerCase() && (
                  <img
                    src={impersonateUserIcon}
                    alt="impersonate user"
                    style={{
                      width: '18px',
                      height: '18px',
                    }}
                  />
                )}
              </div>
              <div className="profile-widget-designation fs-6">
                {employeeDetails?.[0]?.DesignationInt}
              </div>
            </div>
          </div>
          <div className="card-separator" />
          <div
            className="user-contact-details gap-3"
            style={{ color: '#FFFFFF' }}
          >
            <div className="user-phone d-flex flex-row align-items-center gap-2">
              <div>
                <img src={iconsProfilePhone} alt="Profile Phone Icon" />
              </div>
              <div>{employeeContact?.[0]?.LocalMobile}</div>
            </div>
            <div className="separator" />
            <div className="user-email d-flex flex-row align-items-center gap-2">
              <div>
                <img src={iconsProfileEmail} alt="Profile Email Icon" />
              </div>
              <div
                title={employeeDetails?.[0]?.Email}
                style={{ textOverflow: 'ellipsis', overflowX: 'hidden' }}
              >
                {employeeDetails?.[0]?.Email}
              </div>
            </div>
          </div>
        </div>
        {/* Tabs */}
        <div className="profile-widget-tabs">
          <ul className="nav nav-underline nav-profile-tabs d-flex flex-row align-items-center flex-nowrap">
            <button
              type="button"
              onClick={() => toggle('1')}
              className={`profile-nav-link text-nowrap cursor-pointer p-1 w-50 py-2 nav-link nav-link p-0 ${activeTab === '1' ? 'active' : ''}`}
            >
              Personal Info
            </button>
            <button
              type="button"
              onClick={() => toggle('2')}
              className={`profile-nav-link cursor-pointer p-1 w-50 py-2 nav-link nav-link p-0 ${activeTab === '2' ? 'active' : ''}`}
            >
              Work & Reporting Info
            </button>
          </ul>
        </div>
        {/* Tab Content */}
        <div className="profile-widget-tab-content">
          <TabContent activeTab={activeTab}>
            <TabPane tabId="1">
              <ProfileDemoTab
                employeeDetails={employeeDetails}
                passportDoc={passportDoc}
                visaDoc={visaDoc}
              />
            </TabPane>
            <TabPane tabId="2">
              <WorkReportTab employeeDetails={employeeDetails} />
            </TabPane>
          </TabContent>
        </div>
      </CardBody>
      {changeEmpModal && (
        <ChangeEmployeeModal
          isOpen={changeEmpModal}
          toggle={() => setChangeEmpModal(!changeEmpModal)}
        />
      )}
    </Card>
  );
}

export default ProfileWidget;
